import React, { useEffect } from "react";

export function ParsedText({
    children,
    animate = false,
}: {
    children: string;
    animate?: boolean;
}): React.ReactNode[] {
    const parts = children.split(/(\/\/.*?\/\/|\*.*?\*)/g);

    return parts.map((part, index) => {
        if (
            (part.startsWith("//") && part.endsWith("//")) ||
            (part.startsWith("*") && part.endsWith("*"))
        ) {
            // Strip all nested // and * markers to create a single italic element
            let italicText = part;
            while (
                (italicText.startsWith("//") &&
                    italicText.endsWith("//") &&
                    italicText.length > 4) ||
                (italicText.startsWith("*") &&
                    italicText.endsWith("*") &&
                    italicText.length > 2)
            ) {
                if (italicText.startsWith("//") && italicText.endsWith("//")) {
                    italicText = italicText.slice(2, -2);
                } else if (
                    italicText.startsWith("*") &&
                    italicText.endsWith("*")
                ) {
                    italicText = italicText.slice(1, -1);
                }
            }

            const lettersBefore = parts
                .slice(0, index)
                .reduce((sum, p) => sum + p.length, 0);

            if (animate) {
                return (
                    <AnimateItalic
                        key={index}
                        text={italicText}
                        lettersBefore={lettersBefore}
                    />
                );
            }

            return (
                <em key={index} className="italic">
                    {italicText}
                </em>
            );
        } else if (part.trim() !== "") {
            const lettersBefore = parts
                .slice(0, index)
                .reduce((sum, p) => sum + p.length, 0);

            if (animate) {
                return (
                    <AnimateNormal
                        key={index}
                        text={part}
                        lettersBefore={lettersBefore}
                    />
                );
            }

            return <span key={index}>{part}</span>;
        } else {
            return null;
        }
    });
}

function AnimateItalic({
    text,
    lettersBefore,
}: {
    text: string;
    lettersBefore: number;
}) {
    const visibleContent = useAnimatedContent({ text, lettersBefore });

    return <em className="italic">{visibleContent}</em>;
}

function AnimateNormal({
    text,
    lettersBefore,
}: {
    text: string;
    lettersBefore: number;
}) {
    const visibleContent = useAnimatedContent({ text, lettersBefore });

    return <span>{visibleContent}</span>;
}

function useAnimatedContent({
    text,
    lettersBefore,
}: {
    text: string;
    lettersBefore: number;
}) {
    const [visibleContent, setVisibleContent] = React.useState<string>("");

    useEffect(() => {
        const animate = () => {
            let currentIndex = 0;
            const interval = setInterval(() => {
                setVisibleContent(text.slice(0, currentIndex + 1));
                currentIndex++;
                if (currentIndex >= text.length) {
                    clearInterval(interval);
                }
            }, 15);
        };

        if (lettersBefore === 0) {
            animate();
        }
        setTimeout(() => {
            animate();
        }, lettersBefore * 15);
    }, [text]);

    return visibleContent;
}
